/**
 * A day's meals as the week table lists them, read-only: one row per meal that holds something, in the
 * server's order (started at, then creation) — its display name, a quiet start time, its sums, opening
 * onto its products when it holds more than one — then the day total. An empty meal gets no row but
 * keeps its place in the numbering. And the quiet line of meal habits under the trainer's chart.
 *
 * The naming rule is shared with mobile: a meal with no stored name is „Posiłek N" by its 1-based
 * position in the day, a stored pill name reads in the trainer's language, and a name the day already
 * used (ignoring case) gets „(2)", „(3)" in day order.
 */

import type { Translate } from '~/shared/lib/localeTranslate';

import {
  wholeNumber,
  type NutritionAmounts,
  type NutritionDayMeals,
  type NutritionEntry,
  type NutritionMeal,
} from './nutrition';

const KEY = 'web:clientDetail.nutrition';

/** The names the client's pills store, verbatim in Polish, and the key each reads under in a locale. */
export const MEAL_PILLS: Readonly<Record<string, string>> = {
  Śniadanie: 'breakfast',
  'II śniadanie': 'secondBreakfast',
  Obiad: 'lunch',
  Podwieczorek: 'afternoonSnack',
  Kolacja: 'dinner',
  Przekąska: 'snack',
};

export interface MealRowView {
  readonly key: string;
  /** „Posiłek 2", „Obiad (2)", or „Cały dzień" for a day total. */
  readonly title: string;
  /** Grey beside the title: the meal's start „12:40", „suma" for a day total, else nothing. */
  readonly quiet: string | null;
  /** „3 produkty" — only when there is more than one item. */
  readonly count: string | null;
  /** Only a meal of more than one item opens; a single item's meta stands in the meal's own row. */
  readonly expandable: boolean;
  /** The one item of a single-item meal (or a named day total), whose meta the row itself carries. */
  readonly single: MealItemView | null;
  readonly amounts: NutritionAmounts;
  readonly items: readonly MealItemView[];
}

export interface MealItemView {
  readonly key: string;
  readonly name: string;
  readonly brand: string | null;
  /** „80 g", a product's weighed portion. */
  readonly grams: string | null;
  /** „ręcznie" for a hand-typed item. */
  readonly note: string | null;
  /** The Open Food Facts page — the ODbL credit stays wherever a product is shown. */
  readonly sourceUrl: string | null;
  readonly amounts: NutritionAmounts;
}

/** Every meal of the day that holds something, in its order, then the day total. */
export function dayRowViews(day: NutritionDayMeals, t: Translate, locale: string): MealRowView[] {
  const titles = mealDisplayNames(day.meals, t);
  const rows = day.meals.flatMap((meal, index) =>
    meal.items.length === 0 ? [] : [mealRowView(meal, titles[index] ?? '', t, locale)],
  );
  if (day.dayTotal === null) return rows;
  const total = day.dayTotal;
  return [
    ...rows,
    {
      key: `total:${total.key}`,
      title: t(`${KEY}.entryDayTotal`),
      quiet: t(`${KEY}.dayTotalQuiet`),
      count: null,
      expandable: false,
      single: total.name === null ? null : mealItemView(total, t, locale),
      amounts: total.amounts,
      items: [],
    },
  ];
}

/** The meals' display names in day order (see the module's naming rule). */
export function mealDisplayNames(meals: readonly Pick<NutritionMeal, 'name'>[], t: Translate): string[] {
  const seen = new Map<string, number>();
  return meals.map((meal, index) => {
    const stored = meal.name?.trim() || null;
    if (stored === null) return t(`${KEY}.meals.unnamed`, { n: index + 1 });
    const same = stored.toLocaleLowerCase('pl');
    const nth = (seen.get(same) ?? 0) + 1;
    seen.set(same, nth);
    const pill = MEAL_PILLS[stored];
    const name = pill === undefined ? stored : t(`${KEY}.meals.pill.${pill}`);
    return nth === 1 ? name : t(`${KEY}.meals.duplicate`, { name, n: nth });
  });
}

/** `HH:mm` as the clock reads it, „08:05" → „8:05"; anything else is no time. */
export function mealClock(time: string | null): string | null {
  if (time === null) return null;
  const match = /^(\d{2}):(\d{2})/.exec(time);
  if (match === null) return null;
  return `${String(Number(match[1]))}:${match[2] ?? ''}`;
}

function mealRowView(meal: NutritionMeal, title: string, t: Translate, locale: string): MealRowView {
  const views = meal.items.map((entry) => mealItemView(entry, t, locale));
  return {
    key: `meal:${meal.key}`,
    title,
    quiet: mealClock(meal.startedAt),
    count: views.length > 1 ? t(`${KEY}.productCount`, { count: views.length }) : null,
    expandable: views.length > 1,
    single: views.length === 1 ? (views[0] ?? null) : null,
    amounts: meal.amounts,
    items: views,
  };
}

function mealItemView(entry: NutritionEntry, t: Translate, locale: string): MealItemView {
  const grams = entry.product?.grams ?? null;
  return {
    key: entry.key,
    name: entry.name ?? t(`${KEY}.itemUnnamed`),
    brand: entry.product?.brand ?? null,
    grams: grams === null ? null : t(`${KEY}.productGrams`, { grams: wholeNumber(grams, locale) }),
    note: entry.kind === 'manual' ? t(`${KEY}.itemManual`) : null,
    sourceUrl: entry.product?.sourceUrl ?? null,
    amounts: entry.amounts,
  };
}

/** Opening one meal leaves every other one as it was. */
export function toggleMeal(open: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(open);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}
